import { getEncodedState, getEnvVar } from '@zenos/front-end-util-utils';
import { fetchToken, type AuthSession } from '../api/auth';

const GOOGLE_OAUTH_LOAD_TIMEOUT_MS = 5_000;

export function waitForGoogleOauth(
  timeoutMs = GOOGLE_OAUTH_LOAD_TIMEOUT_MS,
): Promise<void> {
  if (typeof google !== 'undefined' && google.accounts?.oauth2) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (typeof google !== 'undefined' && google.accounts?.oauth2) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error('Google Identity Services failed to load'));
      }
    }, 50);
  });
}

export async function requestGoogleAuthCode(): Promise<{
  code: string;
  state: string;
  redirectUri: string;
}> {
  await waitForGoogleOauth();

  const redirectUri = window.location.origin;
  const state = getEncodedState({
    redirectUri,
    nonce: crypto.randomUUID(),
  });

  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initCodeClient({
      state,
      client_id: getEnvVar('clientId'),
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback({ code, state: returnedState }) {
        if (!code || returnedState !== state) {
          reject(new Error('Invalid Google auth response'));
          return;
        }

        resolve({ code, state, redirectUri });
      },
      error_callback(error) {
        reject(error ?? new Error('Google auth failed'));
      },
    });

    client.requestCode();
  });
}

/** Interactive Google sign-in (must be triggered by a user gesture). */
export async function signInWithGoogle(): Promise<AuthSession> {
  const { code, state, redirectUri } = await requestGoogleAuthCode();
  return fetchToken(code, redirectUri, state);
}
